// French copy, namespace `leads` (source language: English is typed against it):
// the pipeline screen, the lead sheet, contacts, key facts, sources, the CSV
// export and the errors of the `/api/leads` routes.

export const leads = {
  page: {
    title: "Leads — Hune",
    subject: "vos leads",
    workspaceBanner: { before: "Pipeline de l’espace client ", after: " (lecture seule)." },
  },

  /** Pipeline statuses, keyed by the stored value. */
  statuses: {
    new: "Nouveau",
    to_contact: "À contacter",
    contacted: "Contacté",
    replied: "A répondu",
    won: "Gagné",
    dismissed: "Écarté",
  },

  /** Dismissal reasons, keyed by the stored value. */
  dismissReasons: {
    off_target: { label: "Pas ma cible", hint: "Secteur ou métier hors ICP" },
    wrong_size: { label: "Mauvaise taille", hint: "Trop petit ou trop gros pour l’offre" },
    competitor: { label: "Concurrent", hint: "Fait le même métier que nous" },
    already_customer: { label: "Déjà client", hint: "Déjà dans notre portefeuille" },
    signal_weak: { label: "Signal peu convaincant", hint: "Le déclencheur ne tient pas" },
    no_contact: { label: "Pas d’interlocuteur", hint: "Aucune voie de contact exploitable" },
    bad_timing: { label: "Mauvais moment", hint: "Bonne cible, pas maintenant" },
    other: { label: "Autre raison", hint: "À préciser" },
  },

  /** The five axes of the detailed score, keyed by the stored field. */
  assessmentAxes: {
    need_intensity: "Intensité du besoin",
    solution_alignment: "Adéquation avec l’offre",
    decision_window: "Fenêtre de décision",
    contact_path: "Voie de contact",
    source_confidence: "Fiabilité de la source",
  },

  /** Signal readiness, keyed by the stored (French) value written by the agent. */
  readiness: {
    "Demande active": "Demande active",
    "Besoin explicite": "Besoin explicite",
    "Fenêtre favorable": "Fenêtre favorable",
  } as Record<string, string>,

  /** Run statuses (reports, dashboard), keyed by the stored value. */
  runStatuses: {
    queued: "En attente",
    in_progress: "En cours",
    completed: "Terminé",
    failed: "Échoué",
    cancelled: "Annulé",
  } as Record<string, string>,

  table: {
    pipeline: "Pipeline",
    count: (n: number) => (n > 1 ? `${n} leads` : `${n} lead`),
    unread: (n: number) => (n > 1 ? `${n} non lus` : `${n} non lu`),
    showAll: "Tout afficher",
    showAllTitle: "Afficher tous les leads",
    unreadOnlyTitle: "N'afficher que les leads jamais ouverts",
    signalsThisWeek: (n: number) => (n > 1 ? `${n} signaux cette semaine` : `${n} signal cette semaine`),
    fedBy: "Alimenté par chaque passage de votre veille",
    searchPlaceholder: "Organisation, signal, catégorie…",
    statusFilterLabel: "Filtrer par statut",
    allStatuses: "Tous",
    layoutLabel: "Présentation",
    cards: "Cartes",
    table: "Tableau",
    favorites: "Favoris",
    archived: "Archivés",
    empty: {
      before: "Aucun lead pour le moment. Votre ",
      link: "veille",
      after: " déposera ici les organisations détectées, à chaque passage.",
    },
    emptyArchived: "Aucun lead archivé.",
    emptyFiltered: "Aucun lead ne correspond à ces filtres.",
    updateFailed: "Mise à jour impossible.",
  },

  /** Cards and dense table of the pipeline. */
  views: {
    addFavoriteNamed: (name: string) => `Ajouter ${name} aux favoris`,
    removeFavoriteNamed: (name: string) => `Retirer ${name} des favoris`,
    archiveNamed: (name: string) => `Archiver ${name}`,
    restoreNamed: (name: string) => `Restaurer ${name}`,
    scoreAria: (score: number) => `Score ${score} sur 100`,
    reactivated: "Réactivé",
    unread: "Non lu",
    contactToFind: "À trouver",
    columns: {
      favorite: "Favori",
      organization: "Organisation",
      signal: "Signal",
      contact: "Interlocuteur",
      score: "Score",
      status: "Statut",
      actions: "Actions",
    },
  },

  /** Actions shared by the list and the sheet. */
  actions: {
    addFavorite: "Ajouter aux favoris",
    removeFavorite: "Retirer des favoris",
    archive: "Archiver",
    restore: "Restaurer",
  },

  sheet: {
    reactivated: "Réactivé",
    signalCount: (n: number) => (n > 1 ? `${n} signaux` : `${n} signal`),
    dismissed: "Écarté",
    fixTargeting: "Corriger le ciblage",
    tabsLabel: "Sections de la fiche",
    tabs: { overview: "Synthèse", contacts: "Contacts", message: "Message", dossier: "Dossier" },
    noResearch: "Aucune enquête menée sur ce lead.",
    prepareApproach: "Préparer l’approche",
  },

  actionMenu: {
    open: "Ouvrir les actions",
    title: "Actions (⌘ K)",
    label: "Actions",
    messageCopied: "Message copié",
    copyMessage: "Copier le message",
    refreshResearch: "Actualiser l’enquête",
    openSource: "Ouvrir la source",
    advance: "Faire avancer",
    dismiss: "Écarter ce lead…",
  },

  /** Bounded score bar of the dossier. */
  linearScoreAria: (label: string, value: number, maximum: number) => `${label} : ${value} sur ${maximum}`,

  /** Contacts tab of the sheet. */
  contacts: {
    emptyTitle: "Aucune carte de décision disponible",
    emptyText: "Les anciens contacts restent accessibles ci-dessous lorsqu’une personne était déjà associée au lead.",
    pickerLabel: "Interlocuteurs possibles",
    profiles: (n: number) => (n > 1 ? `${n} profils` : `${n} profil`),
    committee: "Comité d’approche",
    gaps: (n: number) => (n > 1 ? `${n} informations à confirmer` : `${n} information à confirmer`),
    fullDossier: "Dossier complet",
    proofs: (n: number) => (n > 1 ? `${n} preuves` : `${n} preuve`),
  },

  /** Full portrait of a contact (folded under its card). */
  contactDetails: {
    summary: "Portrait professionnel",
    responsibilities: "Responsabilités documentées",
    background: "Parcours pertinent",
    recentSignals: "Signaux récents",
    source: "Source",
    personalization: "Faits personnalisables",
    publicPresence: "Présence publique",
    otherIcebreakers: "Autres accroches",
    sources: "Sources",
    caveats: "Points à confirmer",
    searches: (n: number) => `${n} recherches web effectuées`,
    profileKinds: {
      linkedin_profile: "Profil LinkedIn",
      linkedin_post: "Publication LinkedIn",
      interview: "Interview",
      article: "Article",
      podcast: "Podcast",
      conference: "Conférence",
      social_post: "Publication",
      other: "Ressource publique",
    },
  },

  /** Message tab of the sheet. */
  message: {
    copied: "Copié",
    subject: "Objet",
    copySubject: "Copier l’objet",
    openInGmail: "Ouvrir dans Gmail",
    otherMail: "Autre messagerie",
    linkedinHandoff: (firstName: string) => `Copier et ouvrir le profil de ${firstName}`,
    linkedinCopied: "Message copié : il reste à le coller dans LinkedIn.",
    writing: "Rédaction…",
    rewrite: "Réécrire",
    writePerContact: "Rédiger un e-mail par contact",
    recipient: "Destinataire",
    wordCount: (n: number) => `E-mail de ${n} mots`,
    writeTo: (name: string) => `Écrire à ${name}`,
    approachMessage: "Message d’approche",
    copyEmail: "Copier l’e-mail",
    followUpHint: "Relance, trois ou quatre jours plus tard, dans le même fil",
    copyFollowUp: "Copier la relance",
    linkedinHint: "Message LinkedIn, une fois la relation acceptée",
    copyLinkedin: "Copier le message LinkedIn",
    whyThisEmail: "Pourquoi cet e-mail",
    copyMessage: "Copier le message",
    legacyNote:
      "Message commun au lead. Un e-mail écrit pour chaque contact, à partir de son portrait, est plus court et plus juste.",
    noEmailYet: "Aucun e-mail n’a encore été rédigé pour ce lead.",
    noContact: (organization: string) =>
      `Aucun interlocuteur identifié chez ${organization} : lancez l’enquête depuis l’onglet Dossier.`,
  },

  /** Dossier tab of the sheet (commercial research). */
  osint: {
    starterEyebrow: "Enquête commerciale",
    starterTitle: "Trouver les signaux et les bons interlocuteurs",
    starterText:
      "Hune croise les résultats publics de l’organisation, ses recrutements, projets, documents, prises de parole et échos fournisseurs, puis prépare les questions et angles de conversation sourcés.",
    running: "Enquête en cours…",
    start: "Lancer l’enquête",
    title: "Dossier prospect",
    refreshing: "Actualisation…",
    refresh: "Actualiser",
    summary: "Lecture en une minute",
    takeaway: "À en tirer",
    noSignals: "Aucun signal suffisamment solide pour qualifier cette organisation.",
    questions: "Questions de découverte",
    copied: "Copiée",
    copy: "Copier",
    researchWork: "Voir le travail de recherche",
    gaps: "Points à vérifier",
    queries: "Recherches exécutées",
    stages: {
      high_intent: "Intention forte",
      problem_aware: "Problème exprimé",
      trigger_present: "Déclencheur présent",
      potential_fit: "Potentiel à confirmer",
    },
    signalTypes: {
      vendor_echo: "Écho fournisseur",
      event: "Congrès ou salon",
      hiring: "Recrutement",
      transformation: "Transformation",
      public_document: "Document public",
      legal_event: "Événement légal",
      procurement: "Marché public",
      advertising: "Campagne",
      technology: "Technologie",
      public_voice: "Prise de parole",
      customer_pain: "Problème exprimé",
      other: "Autre signal",
    },
    scoreAxes: {
      pain_strength: "Douleur observée",
      product_fit: "Adéquation offre",
      timing: "Timing",
      public_reachability: "Voie d’approche",
      evidence_quality: "Qualité des preuves",
    },
  },

  /** The single contact of older leads, before the buying committee. */
  legacyContact: {
    fallbackName: "Interlocuteur",
    theContact: "le contact",
    quotedRole: (role: string) => `« ${role} »`,
    searchOnLinkedin: (who: string) => `Chercher ${who} sur LinkedIn`,
    searchOnGoogle: "Chercher sur Google",
  },

  dismiss: {
    title: (name: string) => `Écarter ${name}`,
    description:
      "La raison n’est pas un formulaire de politesse : elle apprend à l’agent ce qu’il ne doit plus vous proposer. Les raisons de ciblage rejoignent les exclusions de votre ICP.",
    noteLabel: "Précision (facultatif)",
    notePlaceholder: "Ex. mairies de moins de 5 000 habitants, budget inexistant.",
    feedsIcp: "Cette raison remontera dans les exclusions transmises à l’agent au prochain passage.",
    cancel: "Annuler",
    confirm: "Écarter",
  },

  /** Overview tab of the sheet. */
  overview: {
    scoreLabels: { priority: "Prioritaire", promising: "Prometteur", toQualify: "À qualifier" },
    previousLead: "Lead précédent",
    previousTitle: "Précédent (←)",
    nextLead: "Lead suivant",
    nextTitle: "Suivant (→)",
    progress: "Avancement",
    noSignal: "Aucun signal associé à ce lead.",
    quoted: (text: string) => `« ${text} »`,
    whyThem: "Pourquoi eux",
    whyNow: "Pourquoi maintenant",
    toVerify: "Point à vérifier",
    whoToContact: "Qui contacter",
    roleToConfirm: "Fonction à confirmer",
    publicResearch: "Ce que révèlent les recherches publiques",
    scoreOutOf: "Score sur 100",
    status: "Statut",
    approachRoute: "Voie d’approche",
  },

  /** A contact, as shown on a lead (app) or a prospect (admin). */
  contact: {
    decisionRoles: {
      economic_buyer: "Décideur budget",
      final_decision_maker: "Décideur final",
      business_owner: "Responsable métier",
      operational_owner: "Responsable opérationnel",
      influencer: "Prescripteur",
      executive_fallback: "Direction",
    },
    confidence: { high: "Preuves fortes", medium: "À confirmer", low: "Preuves faibles" },
    confidenceUnknown: "Confiance à évaluer",
    lanes: { decision: "Décision", business: "Métier", operations: "Opérations", influence: "Influence" },
    linkedinProfileOf: (name: string) => `Profil LinkedIn de ${name}`,
    linkedinProfile: "Profil LinkedIn",
    roleToConfirm: "Fonction à confirmer",
    searchLinkedin: "Chercher sur LinkedIn",
    searchGoogle: "Chercher sur Google",
    firstToContact: "À contacter en premier",
    priority: (rank: number) => `Priorité ${rank}`,
    whyThisPerson: "Pourquoi cette personne",
    approachAngle: "Angle d’approche",
  },

  /** Qualification criteria answered by the research. */
  keyFacts: {
    title: "Ce qui compte pour votre offre",
    established: (count: number, total: number) =>
      count > 1 ? `${count} critères sur ${total} établis` : `${count} critère sur ${total} établi`,
    decisive: "décisif",
    statuses: { confirmed: "Confirmé", inferred: "Déduit, à recouper", unknown: "Non trouvé" },
    notFound: "Non trouvé",
    ask: "À demander : ",
  },

  /** A link to a source, with its preview card. */
  source: {
    checkFailed: "Vérification impossible pour le moment.",
    fallbackLabel: "Source",
    unreadableLink: (label: string) => `${label} — lien illisible`,
    loading: "Lecture de la page…",
    quoted: (text: string) => `« ${text} »`,
    pageOk: "La page répond.",
    blocked: "Ce site refuse la vérification automatique : ouvrez la page pour la consulter.",
    pageDown: "Cette page ne répond plus.",
    openOnQuote: "Ouvrir sur le passage cité",
    openSource: "Ouvrir la source",
  },

  /** Long tasks of the sheet (research, writing), announced by toasts. */
  tasks: {
    enrich: {
      done: "Enquête terminée",
      failed: "L’enquête n’a pas abouti",
      interrupted: "La connexion a été coupée avant la fin : l’enquête a peut-être continué sur le serveur.",
      retry: "Relancer l’enquête",
    },
    emails: {
      done: "Messages prêts",
      failed: "Les messages n’ont pas pu être rédigés",
      interrupted: "La connexion a été coupée avant la fin : la rédaction a peut-être continué sur le serveur.",
      retry: "Relancer la rédaction",
    },
    noReason: (status: number) => `Le serveur n’a pas donné de raison (erreur ${status}).`,
    reloadHint: (message: string) => `${message} Ouvrez la fiche pour voir où elle en est.`,
    open: "Ouvrir",
    retry: "Réessayer",
    reload: "Recharger la fiche",
  },

  /** Links to look up an organization (site, LinkedIn, registry, map). */
  orgLinks: { map: "Plan" },

  offerLink: {
    unavailable: "Cette offre n’est plus disponible : voici l’offre consultée.",
  },

  /** CSV export: the button, and the file's column headers. */
  export: {
    failed: "L’export a échoué. Réessayez dans un instant.",
    failedShort: "L’export a échoué.",
    truncated: (limit: string) =>
      `Le fichier s’arrête aux ${limit} leads vus le plus récemment : exportez statut par statut pour avoir les autres.`,
    button: "Exporter en CSV",
    emptyPipeline: "Aucun lead à exporter pour le moment : votre veille les déposera ici",
    emptyFiltered: "Aucun lead à exporter avec ces filtres",
    title: (n: number) => `Télécharger ${n > 1 ? `${n} leads` : "1 lead"} en CSV (Excel, Google Sheets)`,
    headers: {
      organization: "Organisation",
      website: "Site web",
      status: "Statut",
      score: "Score",
      latestSignal: "Dernier signal",
      signalDate: "Date du signal",
      signalSource: "Source du signal",
      signalCount: "Nombre de signaux",
      firstSeen: "Premier repérage",
      dismissReason: "Raison d’écartement",
      contact: "Interlocuteur",
      role: "Fonction",
      email: "E-mail",
      linkedin: "LinkedIn",
      subject: "Objet du message",
      message: "Message",
      followUp: "Relance",
      link: "Lien Hune",
    },
  },

  /** Errors of the `/api/leads` and `/api/views/leads` routes. */
  errors: {
    signIn: "Connectez-vous.",
    loadFailed: "Impossible de charger les leads. Vérifiez que les migrations du pipeline sont appliquées.",
    exportSessionExpired: "Votre session a expiré : reconnectez-vous pour exporter vos leads.",
    exportUnknownFilter: "Filtre d’export inconnu.",
    updateSignIn: "Connectez-vous pour gérer vos leads.",
    invalidBody: "Corps de requête invalide.",
    unknownStatus: "Statut inconnu.",
    notInTeam: "Cette personne ne fait pas partie de votre équipe.",
    invalidFavorite: "Valeur de favori invalide.",
    invalidArchive: "Valeur d'archivage invalide.",
    unknownDismissReason: "Raison d’écartement inconnue.",
    dismissReasonNeedsDismissed: "Une raison d’écartement ne s’applique qu’à un lead écarté.",
    nothingToUpdate: "Rien à mettre à jour.",
    notFound: "Lead introuvable ou accès refusé.",
    emailsSignIn: "Connectez-vous pour rédiger ces e-mails.",
    writerNotConfigured: "Le modèle de rédaction n’est pas configuré sur ce serveur.",
    noContactToWrite: "Aucun interlocuteur à qui écrire : lancez d’abord l’enquête sur ce lead.",
    enrichSignIn: "Connectez-vous pour enrichir ce lead.",
    searchNotConfigured: "La recherche web n’est pas configurée sur ce serveur.",
    researchFailed: "Les recherches n’ont pas pu être menées à terme. Réessayez dans un instant.",
  },
};
